"use client";

import { useTranslations } from "next-intl";
import { Button } from "@/components/ui/button";
import { useFetchCandidates } from "@/hooks/dao/election/useFetchCandidates";
import { useCurrentCycle } from "@/hooks/dao/useCurrentCycle";
import { CandidatesEmptyState } from "./CandidatesEmptyState";
import { CandidatesSkeleton } from "./CandidatesSkeleton";
import { ElectionRow } from "./ElectionRow";
import { formatVoteCount } from "./VoteCountBadge";

/**
 * Largest number of characters any vote-count tile in the current dataset
 * needs to display. The list passes this to ElectionRow → VoteCountBadge so
 * every tile shares the longest tile's width — uniform rows, no clipping. A
 * minimum of 4 keeps "0.00"-shaped rows from looking weirdly squished.
 */
function maxVoteCountDigits(
  rows: { totalReceivedVoteDnmWei: bigint }[],
): number {
  let max = 4;
  for (const r of rows) {
    const len = formatVoteCount(r.totalReceivedVoteDnmWei).length;
    if (len > max) max = len;
  }
  return max;
}

export const CandidatesList: React.FC = () => {
  const t = useTranslations("dao");
  const tCommon = useTranslations("common");
  const { cycle } = useCurrentCycle();
  const { data, isLoading, isError, refetch } = useFetchCandidates(cycle);

  if (isLoading) return <CandidatesSkeleton />;

  if (isError) {
    return (
      <div className="flex flex-col items-center gap-3 py-8 text-center">
        <p className="text-sm text-semantic-error">
          {t("election.candidates.loadError")}
        </p>
        <Button
          variant="light"
          colors="accent"
          size="sm"
          onClick={() => refetch()}
        >
          {tCommon("retry")}
        </Button>
      </div>
    );
  }

  // B2 fix — previously we filtered out rows with no display identity to hide
  // the brief race window where chain has a candidate but backend insert
  // hasn't completed. That filter ALSO permanently hid candidates whose
  // backend POST failed (the original "on-chain but invisible" bug). Chain is
  // authoritative for who's a candidate, so we render every chain row;
  // ElectionRow's displayName cascade falls through to the wallet address
  // (via WalletAddress) when nickname is null, giving the submitter a
  // visible row + a vote button as soon as their tx confirms.
  const visible = data ?? [];
  const tileDigits = maxVoteCountDigits(visible);

  if (visible.length === 0) {
    return <CandidatesEmptyState />;
  }

  return (
    <div className="flex flex-col gap-3">
      {visible.map((candidate) => (
        <ElectionRow
          key={candidate.walletAddress}
          candidate={candidate}
          voteTileDigitsCh={tileDigits}
        />
      ))}
    </div>
  );
};
